import { useCallback } from 'react';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Trash2, GripVertical, Plus } from 'lucide-react';
import type { Condition, ComparisonOperator, UniversalCondition } from '@/types/policy-editor';

const EXPRESSION_TYPES = [
    { value: 'comparison', label: 'Compare values' },
    { value: 'membership', label: 'Check membership' },
    { value: 'assignment', label: 'Assign variable' },
    { value: 'iteration', label: 'Iterate collection' },
    { value: 'universal', label: 'For every (universal)' },
    { value: 'negation', label: 'Negate expression' },
    { value: 'functionCall', label: 'Call function' },
    { value: 'raw', label: 'Custom expression' },
] as const;

const COMPARISON_OPERATORS: { value: ComparisonOperator; label: string }[] = [
    { value: '==', label: 'equals' },
    { value: '!=', label: 'not equals' },
    { value: '<', label: 'less than' },
    { value: '>', label: 'greater than' },
    { value: '<=', label: 'at most' },
    { value: '>=', label: 'at least' },
];

interface ExpressionRowProps {
    condition: Condition;
    onChange: (updated: Condition) => void;
    onDelete: () => void;
    readOnly?: boolean;
    showAndLabel?: boolean;
}

export function ExpressionRow({
    condition,
    onChange,
    onDelete,
    readOnly = false,
    showAndLabel = false,
}: ExpressionRowProps) {
    const handleTypeChange = useCallback(
        (newType: string) => {
            const defaults: Record<string, Condition> = {
                comparison: { type: 'comparison', left: '', operator: '==', right: '' },
                membership: { type: 'membership', term: '', collection: '' },
                assignment: { type: 'assignment', variable: '', expression: '' },
                iteration: { type: 'iteration', variables: [''], collection: '' },
                universal: { type: 'universal', variables: ['item'], collection: '', body: [] },
                negation: { type: 'negation', expression: '' },
                functionCall: { type: 'functionCall', name: '', args: [] },
                raw: { type: 'raw', text: '' },
            };
            onChange(defaults[newType] ?? { type: 'raw', text: '' });
        },
        [onChange],
    );

    return (
        <div className="flex flex-col gap-1">
            {showAndLabel && (
                <span className="ml-8 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    AND
                </span>
            )}
            <div className="flex items-start gap-2">
                {!readOnly && (
                    <div className="mt-2 cursor-grab text-slate-300 hover:text-slate-500">
                        <GripVertical size={14} />
                    </div>
                )}

                <Select
                    value={condition.type}
                    onValueChange={handleTypeChange}
                    disabled={readOnly}
                >
                    <SelectTrigger className="h-8 w-[160px] shrink-0 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {EXPRESSION_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                                {t.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <div className="flex flex-1 flex-wrap items-center gap-1.5">
                    {condition.type === 'comparison' && (
                        <ComparisonFields condition={condition} onChange={onChange} readOnly={readOnly} />
                    )}
                    {condition.type === 'membership' && (
                        <MembershipFields condition={condition} onChange={onChange} readOnly={readOnly} />
                    )}
                    {condition.type === 'assignment' && (
                        <AssignmentFields condition={condition} onChange={onChange} readOnly={readOnly} />
                    )}
                    {condition.type === 'iteration' && (
                        <IterationFields condition={condition} onChange={onChange} readOnly={readOnly} />
                    )}
                    {condition.type === 'universal' && (
                        <UniversalFields condition={condition} onChange={onChange} readOnly={readOnly} />
                    )}
                    {condition.type === 'negation' && (
                        <NegationFields condition={condition} onChange={onChange} readOnly={readOnly} />
                    )}
                    {condition.type === 'functionCall' && (
                        <FunctionCallFields condition={condition} onChange={onChange} readOnly={readOnly} />
                    )}
                    {condition.type === 'raw' && (
                        <RawFields condition={condition} onChange={onChange} readOnly={readOnly} />
                    )}
                </div>

                {!readOnly && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-slate-400 hover:text-red-500"
                        onClick={onDelete}
                    >
                        <Trash2 size={14} />
                    </Button>
                )}
            </div>
        </div>
    );
}

function ComparisonFields({
    condition,
    onChange,
    readOnly,
}: {
    condition: Extract<Condition, { type: 'comparison' }>;
    onChange: (c: Condition) => void;
    readOnly?: boolean;
}) {
    return (
        <>
            <Input
                className="h-8 min-w-[180px] flex-1 font-mono text-xs"
                placeholder="e.g. input.subject.tier"
                value={condition.left}
                onChange={(e) => onChange({ ...condition, left: e.target.value })}
                disabled={readOnly}
                title={condition.left}
            />
            <Select
                value={condition.operator}
                onValueChange={(op) =>
                    onChange({ ...condition, operator: op as ComparisonOperator })
                }
                disabled={readOnly}
            >
                <SelectTrigger className="h-8 w-[110px] text-xs">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {COMPARISON_OPERATORS.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                            {op.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Input
                className="h-8 min-w-[180px] flex-1 font-mono text-xs"
                placeholder='e.g. "enterprise"'
                value={condition.right}
                onChange={(e) => onChange({ ...condition, right: e.target.value })}
                disabled={readOnly}
                title={condition.right}
            />
        </>
    );
}

function MembershipFields({
    condition,
    onChange,
    readOnly,
}: {
    condition: Extract<Condition, { type: 'membership' }>;
    onChange: (c: Condition) => void;
    readOnly?: boolean;
}) {
    return (
        <>
            <Input
                className="h-8 min-w-[140px] flex-1 font-mono text-xs"
                placeholder="value"
                value={condition.term}
                onChange={(e) => onChange({ ...condition, term: e.target.value })}
                disabled={readOnly}
                title={condition.term}
            />
            <span className="text-xs text-slate-500">
                {condition.negated ? 'not in' : 'in'}
            </span>
            <Input
                className="h-8 min-w-[140px] flex-1 font-mono text-xs"
                placeholder="collection"
                value={condition.collection}
                onChange={(e) => onChange({ ...condition, collection: e.target.value })}
                disabled={readOnly}
                title={condition.collection}
            />
        </>
    );
}

function AssignmentFields({
    condition,
    onChange,
    readOnly,
}: {
    condition: Extract<Condition, { type: 'assignment' }>;
    onChange: (c: Condition) => void;
    readOnly?: boolean;
}) {
    return (
        <>
            <Input
                className="h-8 w-[120px] font-mono text-xs"
                placeholder="variable"
                value={condition.variable}
                onChange={(e) => onChange({ ...condition, variable: e.target.value })}
                disabled={readOnly}
            />
            <span className="text-xs text-slate-500">:=</span>
            <Input
                className="h-8 min-w-[180px] flex-1 font-mono text-xs"
                placeholder="expression"
                value={condition.expression}
                onChange={(e) => onChange({ ...condition, expression: e.target.value })}
                disabled={readOnly}
            />
        </>
    );
}

function IterationFields({
    condition,
    onChange,
    readOnly,
}: {
    condition: Extract<Condition, { type: 'iteration' }>;
    onChange: (c: Condition) => void;
    readOnly?: boolean;
}) {
    return (
        <>
            <span className="text-xs text-slate-500">some</span>
            <Input
                className="h-8 w-[120px] font-mono text-xs"
                placeholder="variable"
                value={condition.variables[0] ?? ''}
                onChange={(e) =>
                    onChange({ ...condition, variables: [e.target.value, ...condition.variables.slice(1)] })
                }
                disabled={readOnly}
            />
            <span className="text-xs text-slate-500">in</span>
            <Input
                className="h-8 min-w-[180px] flex-1 font-mono text-xs"
                placeholder="collection"
                value={condition.collection}
                onChange={(e) => onChange({ ...condition, collection: e.target.value })}
                disabled={readOnly}
            />
        </>
    );
}

function NegationFields({
    condition,
    onChange,
    readOnly,
}: {
    condition: Extract<Condition, { type: 'negation' }>;
    onChange: (c: Condition) => void;
    readOnly?: boolean;
}) {
    return (
        <>
            <span className="text-xs font-medium text-red-500">NOT</span>
            <Input
                className="h-8 min-w-[200px] flex-1 font-mono text-xs"
                placeholder="expression"
                value={condition.expression}
                onChange={(e) => onChange({ ...condition, expression: e.target.value })}
                disabled={readOnly}
            />
        </>
    );
}

function FunctionCallFields({
    condition,
    onChange,
    readOnly,
}: {
    condition: Extract<Condition, { type: 'functionCall' }>;
    onChange: (c: Condition) => void;
    readOnly?: boolean;
}) {
    return (
        <>
            {condition.assignTo != null && (
                <>
                    <Input
                        className="h-8 w-[100px] font-mono text-xs"
                        placeholder="result"
                        value={condition.assignTo}
                        onChange={(e) => onChange({ ...condition, assignTo: e.target.value })}
                        disabled={readOnly}
                    />
                    <span className="text-xs text-slate-500">:=</span>
                </>
            )}
            <Input
                className="h-8 w-[140px] font-mono text-xs"
                placeholder="function name"
                value={condition.name}
                onChange={(e) => onChange({ ...condition, name: e.target.value })}
                disabled={readOnly}
            />
            <span className="text-xs text-slate-500">(</span>
            <Input
                className="h-8 min-w-[160px] flex-1 font-mono text-xs"
                placeholder="arg1, arg2, ..."
                value={condition.args.join(', ')}
                onChange={(e) =>
                    onChange({
                        ...condition,
                        args: e.target.value.split(',').map((s) => s.trim()),
                    })
                }
                disabled={readOnly}
            />
            <span className="text-xs text-slate-500">)</span>
        </>
    );
}

function UniversalFields({
    condition,
    onChange,
    readOnly,
}: {
    condition: UniversalCondition;
    onChange: (c: Condition) => void;
    readOnly?: boolean;
}) {
    const handleBodyChange = (index: number, updated: Condition) => {
        const next = [...condition.body];
        next[index] = updated;
        onChange({ ...condition, body: next });
    };

    const handleBodyDelete = (index: number) => {
        onChange({ ...condition, body: condition.body.filter((_, i) => i !== index) });
    };

    const handleBodyAdd = () => {
        onChange({
            ...condition,
            body: [...condition.body, { type: 'membership', term: '', collection: '' }],
        });
    };

    return (
        <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-violet-600">every</span>
                <Input
                    className="h-7 w-[100px] font-mono text-xs"
                    placeholder="var"
                    value={condition.variables[0] ?? ''}
                    onChange={(e) =>
                        onChange({ ...condition, variables: [e.target.value, ...condition.variables.slice(1)] })
                    }
                    disabled={readOnly}
                />
                <span className="text-xs text-slate-500">in</span>
                <Input
                    className="h-7 min-w-[160px] flex-1 font-mono text-xs"
                    placeholder="collection"
                    value={condition.collection}
                    onChange={(e) => onChange({ ...condition, collection: e.target.value })}
                    disabled={readOnly}
                    title={condition.collection}
                />
            </div>
            <div className="ml-4 space-y-1.5 rounded-md border border-violet-100 bg-violet-50/30 p-2">
                <span className="text-[10px] font-medium text-violet-400">Body (all must hold)</span>
                {condition.body.map((cond, i) => (
                    <ExpressionRow
                        key={i}
                        condition={cond}
                        onChange={(c) => handleBodyChange(i, c)}
                        onDelete={() => handleBodyDelete(i)}
                        readOnly={readOnly}
                        showAndLabel={i > 0}
                    />
                ))}
                {!readOnly && (
                    <Button variant="ghost" size="sm" className="mt-1 h-6 text-[10px] text-violet-500" onClick={handleBodyAdd}>
                        <Plus size={10} className="mr-0.5" /> Add body condition
                    </Button>
                )}
            </div>
        </div>
    );
}

function RawFields({
    condition,
    onChange,
    readOnly,
}: {
    condition: Extract<Condition, { type: 'raw' }>;
    onChange: (c: Condition) => void;
    readOnly?: boolean;
}) {
    return (
        <Input
            className="h-8 flex-1 font-mono text-xs"
            placeholder="Rego expression"
            value={condition.text}
            onChange={(e) => onChange({ ...condition, text: e.target.value })}
            disabled={readOnly}
        />
    );
}
